import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { toast } from 'react-toastify'
import { getLiveTasks } from 'features/operations/opsLiveTasksSlice'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Button } from 'components/ui/Button/Button'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IOpsComponentValue, IOpsTaskHistoryEntry } from 'types/operationsControl.interface'
import { IOpsTaskAction } from 'types/opsTaskDetail.interface'
import { etaLabel, TASK_ENUM_SUBSTATE, taskCoreState } from 'pages/Operations/Shared/opsControl'
import { buildTaskExtras } from 'pages/Operations/Shared/opsTaskDetailExtras'
import { deptName } from 'pages/Operations/Shared/opsDepartments'
import { CoreStatePill, SourceBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import {
  ActionsList,
  CalloutRow,
  ColumnStack,
  DetailGrid,
  FieldCell,
  FieldLabel,
  FieldValue,
  HeaderRow,
  HistoryActor,
  HistoryBody,
  HistoryDot,
  HistoryHead,
  HistoryItem,
  HistoryList,
  HistoryNote,
  HistoryRail,
  HistoryStem,
  HistoryTime,
  ProvenanceMain,
  ProvenanceRow,
  ProvenanceText,
  StateChip,
  SubStateText,
  TintedBlock,
  TintedText,
  TintedTitle,
  TransitionArrow,
  TransitionWrap,
  TwoColGrid,
} from './OpsTaskDetail.styled'

const taskActions = (taskStatus: number): IOpsTaskAction[] => {
  if (taskStatus === 0) {
    return [
      { label: 'Assign now', variant: 'primary' },
      { label: 'Cancel task', variant: 'secondary' },
    ]
  }
  if (taskStatus === 1) {
    return [
      { label: 'Pause', variant: 'secondary' },
      { label: 'Stop', variant: 'danger' },
      { label: 'Reassign', variant: 'black' },
    ]
  }
  if (taskStatus === 2 || taskStatus === 5 || taskStatus === 6) {
    return [
      { label: 'Resume', variant: 'primary' },
      { label: 'Stop', variant: 'danger' },
    ]
  }
  if (taskStatus === 4) {
    return [
      { label: 'Mark reviewed & unblock', variant: 'primary' },
      { label: 'Reassign', variant: 'secondary' },
    ]
  }
  return []
}

const HistoryRow = ({ entry, last }: { entry: IOpsTaskHistoryEntry; last: boolean }) => (
  <HistoryItem>
    <HistoryRail>
      <HistoryDot />
      {!last && <HistoryStem />}
    </HistoryRail>
    <HistoryBody>
      <HistoryHead>
        <HistoryTime>{entry.at}</HistoryTime>
        <BodyText weight='600'>{entry.action}</BodyText>
        <HistoryActor>{entry.actor}</HistoryActor>
        {entry.fromState && entry.toState && (
          <TransitionWrap>
            <StateChip>{entry.fromState}</StateChip>
            <TransitionArrow>→</TransitionArrow>
            <StateChip>{entry.toState}</StateChip>
          </TransitionWrap>
        )}
      </HistoryHead>
      {entry.note && (
        <HistoryNote $tone={entry.toState === TASK_ENUM_SUBSTATE[4] ? 'danger' : 'warning'}>{entry.note}</HistoryNote>
      )}
    </HistoryBody>
  </HistoryItem>
)

export const OpsTaskDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const apiTasks = useSelector((s: RootState) => s.opsLiveTasks.tasks)

  useEffect(() => {
    dispatch(getLiveTasks({}))
  }, [dispatch])

  const task = apiTasks.find((t) => t.id === Number(id))

  if (!task) {
    return (
      <>
        <NavBar />
        <PageWrapper>
          <H1WithBack title='Task' arrow onClick={() => navigate(-1)} />
          <NoData text='Task not found — it may exist only on the connected backend.' />
        </PageWrapper>
      </>
    )
  }

  const extras = buildTaskExtras(task)
  const actions = taskActions(task.taskStatus)
  const sourcePath =
    task.source === 'PLAN' && task.sourceId != null
      ? `/operations/plans/${task.sourceId}`
      : task.source === 'SHIFT_PROGRAM' && task.sourceId != null
        ? `/operations/shift-programs/${task.sourceId}`
        : task.source === 'TRIGGER'
          ? '/operations/triggers'
          : null

  const detailFields: IOpsComponentValue[] = [
    { label: 'Location', value: task.location },
    { label: 'Department', value: deptName(task.department) },
    { label: 'Machine', value: task.machine ?? '—' },
    { label: 'Operator', value: task.operator ?? '—' },
    { label: 'ETA', value: etaLabel(task.etaMin) },
    { label: 'Created at', value: task.generatedAt },
    ...extras.componentValues,
  ]

  return (
    <>
      <NavBar />
      <PageWrapper>
        <HeaderRow>
          <H1WithBack title={task.name} arrow onClick={() => navigate(-1)} />
          <CoreStatePill state={taskCoreState(task.taskStatus)} context='task' />
          <SubStateText>{TASK_ENUM_SUBSTATE[task.taskStatus]}</SubStateText>
          <SourceBadge source={task.source} />
        </HeaderRow>
        <Container>
          <ContainerContent>
            <ProvenanceRow>
              <ProvenanceMain>
                <ProvenanceText>
                  Created by {task.sourceName ?? 'Manual entry'} · {task.generatedAt}
                </ProvenanceText>
                <OtherText grey>This record cannot be edited</OtherText>
              </ProvenanceMain>
              {sourcePath && (
                <Button variant='secondary' size='sm' onClick={() => navigate(sourcePath)}>
                  Open source
                </Button>
              )}
            </ProvenanceRow>
          </ContainerContent>
        </Container>
        <TwoColGrid>
          <ColumnStack>
            <Container>
              <ContainerHeader>
                <TitleText weight='bold'>Details</TitleText>
              </ContainerHeader>
              <ContainerContent>
                <DetailGrid>
                  {detailFields.map((field) => (
                    <FieldCell key={field.label}>
                      <FieldLabel>{field.label}</FieldLabel>
                      <FieldValue>{field.value}</FieldValue>
                    </FieldCell>
                  ))}
                  {task.blockedReason && <CalloutRow $tone='danger'>{task.blockedReason}</CalloutRow>}
                  {task.unassignedReason && <CalloutRow $tone='warning'>{task.unassignedReason}</CalloutRow>}
                </DetailGrid>
              </ContainerContent>
            </Container>
            <Container>
              <ContainerHeader>
                <TitleText weight='bold'>What happened</TitleText>
              </ContainerHeader>
              <ContainerContent>
                <HistoryList>
                  {extras.history.map((entry, i) => (
                    <HistoryRow key={entry.id} entry={entry} last={i === extras.history.length - 1} />
                  ))}
                </HistoryList>
              </ContainerContent>
            </Container>
          </ColumnStack>
          <ColumnStack>
            <Container>
              <ContainerHeader>
                <TitleText weight='bold'>Assignment</TitleText>
              </ContainerHeader>
              <ContainerContent gap='0.75rem'>
                <BodyText>{extras.resolverNote}</BodyText>
                {extras.overrideNote && (
                  <TintedBlock $tone='warning'>
                    <TintedTitle $tone='warning'>Manual override</TintedTitle>
                    <TintedText>{extras.overrideNote}</TintedText>
                    <OtherText grey>We log who changed it and why</OtherText>
                  </TintedBlock>
                )}
                {task.unassignedReason && (
                  <TintedBlock $tone='danger'>
                    <TintedTitle $tone='danger'>Unassigned — {task.unassignedReason}</TintedTitle>
                    <Button
                      variant='secondary'
                      size='sm'
                      onClick={() => toast.success('Reassignment request queued (mock)')}
                    >
                      Request reassignment
                    </Button>
                  </TintedBlock>
                )}
              </ContainerContent>
            </Container>
            <Container>
              <ContainerHeader>
                <TitleText weight='bold'>Actions</TitleText>
              </ContainerHeader>
              <ContainerContent>
                {actions.length ? (
                  <ActionsList>
                    {actions.map((action) => (
                      <Button
                        key={action.label}
                        variant={action.variant}
                        onClick={() => toast.success(`${action.label} queued (mock)`)}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </ActionsList>
                ) : (
                  <OtherText grey>This task is finished. Corrections are logged as new events.</OtherText>
                )}
              </ContainerContent>
            </Container>
          </ColumnStack>
        </TwoColGrid>
      </PageWrapper>
    </>
  )
}
